import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    styled,
    useTheme,
    IconButton,
    Tooltip,
    Paper,
    Chip,
    Typography,
    CircularProgress,
    Avatar,
    Collapse,
    Card,
    Divider,
    Pagination,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";
import { Refresh, AttachFile, Phone, Email, KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import FlexBetween from "components/flexbox/FlexBetween";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import { Fragment, useState } from "react";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    padding: "16px 20px",
    color: theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black,
    borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
    whiteSpace: "nowrap",
    "&.header": {
        fontWeight: 600,
        fontSize: "0.875rem",
        backgroundColor: theme.palette.mode === 'dark' 
            ? alpha(theme.palette.primary.main, 0.15) 
            : alpha(theme.palette.primary.main, 0.04),
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:hover": {
        backgroundColor: theme.palette.mode === 'dark' 
            ? "rgba(255, 255, 255, 0.05)" 
            : "rgba(0, 0, 0, 0.05)",
    },
}));

const FieldVisitTable = ({ fieldVisits, searchQuery, loading, onRefresh, pagination = null, onPageChange, onPageSizeChange, pageSizeOptions = [10, 25, 50, 100] }) => {
const theme = useTheme();
    const [openRow, setOpenRow] = useState(null);

    const filteredFieldVisits = fieldVisits.filter(visit => {
        // Apply search filter (category filtering is now done server-side via API)
        if (searchQuery) {
            const searchLower = searchQuery.toLowerCase();
            
            // Check if search matches product_subcategory
            const productSubcategoryMatch = visit.product_subcategory?.some(product => 
                product.name?.toLowerCase().includes(searchLower) ||
                product.sku?.toLowerCase().includes(searchLower)
            );
            
            // Check if search matches crop
            const cropMatch = visit.crop?.name?.toLowerCase().includes(searchLower) ||
                (typeof visit.crop === 'string' && visit.crop.toLowerCase().includes(searchLower));
            
            const matchesSearch = (
                visit.farmer_name?.toLowerCase().includes(searchLower) ||
                visit.village?.toLowerCase().includes(searchLower) ||
                visit.district?.toLowerCase().includes(searchLower) ||
                visit.executive_name?.toLowerCase().includes(searchLower) ||
                visit.farmer_email?.toLowerCase().includes(searchLower) ||
                visit.farmer_contact?.includes(searchLower) ||
                visit.product_vertical?.name?.toLowerCase().includes(searchLower) ||
                cropMatch ||
                productSubcategoryMatch
            );
            
            if (!matchesSearch) return false;
        }
        
        return true;
    });

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    const getInitials = (name) => {
        if (!name) return 'N/A';
        const nameParts = name.split(' ');
        if (nameParts.length >= 2) {
            return `${nameParts[0].charAt(0)}${nameParts[1].charAt(0)}`.toUpperCase();
        }
        return name.charAt(0).toUpperCase();
    };

    const handleFileView = (fileUrl) => {
        if (fileUrl) {
            // Assuming the API base URL needs to be prepended
            const fullUrl = fileUrl.startsWith('http') ? fileUrl : `${API_ENDPOINTS.BASE_URL || ''}${fileUrl}`;
            window.open(fullUrl, '_blank');
        }
    };

    return (
        <Box sx={{ width: "100%", p: 2 }}>
            <FlexBetween mb={2} px={1}>
                <Typography variant="h6" fontWeight={600}>
                    Field Visit Records
                </Typography>
                <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                    {/* <Typography variant="body2" color="text.secondary">
                        {pagination?.total_items != null
                            ? (pagination.total_items > 0
                                ? `Showing ${(pagination.current_page - 1) * pagination.page_size + 1} - ${Math.min(pagination.current_page * pagination.page_size, pagination.total_items)} of ${pagination.total_items}`
                                : 'Total: 0 visits')
                            : `Total: ${filteredFieldVisits.length} ${filteredFieldVisits.length === 1 ? 'visit' : 'visits'}`}
                    </Typography> */}
                    {pagination && onPageSizeChange && (
                        <FormControl size="small" sx={{ minWidth: 110 }}>
                            <InputLabel>Rows</InputLabel>
                            <Select value={pagination.page_size} label="Rows" onChange={onPageSizeChange}>
                                {pageSizeOptions.map((opt) => (
                                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                    <Tooltip title="Refresh">
                        <IconButton onClick={onRefresh} disabled={loading} size="small">
                            <Refresh />
                        </IconButton>
                    </Tooltip>
                </Box>
            </FlexBetween>

            {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" py={8}>
                    <CircularProgress />
                    <Typography variant="body1" sx={{ ml: 2 }}>Loading field visits...</Typography>
                </Box>
            ) : filteredFieldVisits.length > 0 ? (
                <TableContainer component={Paper} sx={{ maxHeight: 500, overflow: 'auto',   /* Firefox */
            scrollbarWidth: "thin",
            scrollbarColor:
              theme.palette.mode === "dark"
                ? "#555 #1e1e1e"
                : "#c1c1c1 #f1f1f1",

            /* Chrome, Edge, Safari */
            "&::-webkit-scrollbar": {
              height: 8,
              width: 8,
            },
            "&::-webkit-scrollbar-track": {
              background:
                theme.palette.mode === "dark"
                  ? "#1e1e1e"
                  : "#f1f1f1",
              borderRadius: 8,
            },
            "&::-webkit-scrollbar-thumb": {
              background:
                theme.palette.mode === "dark"
                  ? "#555"
                  : "#c1c1c1",
              borderRadius: 8,
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background:
                theme.palette.mode === "dark"
                  ? "#777"
                  : "#a8a8a8",
            }, }}>
                    <Table stickyHeader>
                        <TableHead sx={{ position: 'sticky', top: 0, zIndex: 20 }}>
                            <TableRow sx={{ backgroundColor: (theme) => theme.palette.grey[200] }}>
                                <StyledTableCell className="header" style={{ padding: "16px", width: 50, backgroundColor: (theme) => theme.palette.grey[200] }}>Expand</StyledTableCell>
                                <StyledTableCell className="header" style={{ padding: "16px", backgroundColor: (theme) => theme.palette.grey[200] }}>Sr.No.</StyledTableCell>
                                <StyledTableCell className="header" style={{ padding: "16px", backgroundColor: (theme) => theme.palette.grey[200] }}>Visit Date</StyledTableCell>
                                <StyledTableCell className="header" style={{ padding: "16px", backgroundColor: (theme) => theme.palette.grey[200] }}>Farmer Details</StyledTableCell>
                                <StyledTableCell className="header" style={{ padding: "16px", backgroundColor: (theme) => theme.palette.grey[200] }}>Location</StyledTableCell>
                                <StyledTableCell className="header" style={{ padding: "16px", backgroundColor: (theme) => theme.palette.grey[200] }}>Contact</StyledTableCell>
                                <StyledTableCell className="header" style={{ padding: "16px", backgroundColor: (theme) => theme.palette.grey[200] }}>Product Vertical</StyledTableCell>
                                <StyledTableCell className="header" style={{ padding: "16px", backgroundColor: (theme) => theme.palette.grey[200] }}>Crop</StyledTableCell>
                                <StyledTableCell className="header" style={{ padding: "16px", backgroundColor: (theme) => theme.palette.grey[200] }}>Products</StyledTableCell>
                                <StyledTableCell className="header" style={{ padding: "16px", backgroundColor: (theme) => theme.palette.grey[200] }}>Farm Area</StyledTableCell>
                                <StyledTableCell className="header" style={{ padding: "16px", backgroundColor: (theme) => theme.palette.grey[200] }}>Plantation Date</StyledTableCell>
                                <StyledTableCell className="header" style={{ padding: "16px", backgroundColor: (theme) => theme.palette.grey[200] }}>Observations</StyledTableCell>
                                <StyledTableCell className="header" style={{ padding: "16px", backgroundColor: (theme) => theme.palette.grey[200] }}>Recommendations</StyledTableCell>
                                <StyledTableCell className="header" style={{ padding: "16px", backgroundColor: (theme) => theme.palette.grey[200] }}>Executive</StyledTableCell>
                                <StyledTableCell className="header" style={{ padding: "16px", backgroundColor: (theme) => theme.palette.grey[200] }}>Feedback</StyledTableCell>
                                <StyledTableCell className="header" style={{ padding: "16px", backgroundColor: (theme) => theme.palette.grey[200] }}>Upload File</StyledTableCell>
                                <StyledTableCell className="header" style={{ padding: "16px", backgroundColor: (theme) => theme.palette.grey[200] }}>Added By</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredFieldVisits.map((visit, index) => {
                                const isOpen = openRow === visit.id;
                                return (
                                    <Fragment key={visit.id}>
                                        <StyledTableRow>
                                            <StyledTableCell style={{padding:"16px"}}>
                                                <IconButton size="small" onClick={() => setOpenRow(isOpen ? null : visit.id)}>
                                                    {isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                                </IconButton>
                                            </StyledTableCell>
                                    <StyledTableCell style={{padding:"16px"}}>
                                        <Chip 
                                            label={`#${index + 1}`} 
                                            size="small" 
                                            color="primary" 
                                            variant="outlined" 
                                        />
                                    </StyledTableCell>
                                    
                                    <StyledTableCell>
                                        <Typography variant="body2" fontWeight={500}>
                                            {formatDate(visit.visit_date)}
                                        </Typography>
                                    </StyledTableCell>
                                    
                                    <StyledTableCell>
                                        <Box display="flex" alignItems="center" gap={1.5}>
                                            <Avatar
                                                sx={{ 
                                                    width: 36, 
                                                    height: 36,
                                                    bgcolor: theme.palette.primary.main,
                                                    fontSize: '0.875rem',
                                                    fontWeight: 600
                                                }}
                                            >
                                                {getInitials(visit.farmer_name)}
                                            </Avatar>
                                            <Box>
                                                <Tooltip title={visit.farmer_name || 'N/A'} arrow>
                                                    <Typography 
                                                        variant="subtitle2" 
                                                        fontWeight={600}
                                                        sx={{ 
                                                            maxWidth: 150, 
                                                            overflow: 'hidden', 
                                                            textOverflow: 'ellipsis', 
                                                            whiteSpace: 'nowrap',
                                                            cursor: 'help'
                                                        }}
                                                    >
                                                        {visit.farmer_name || 'N/A'}
                                                    </Typography>
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                    </StyledTableCell>
                                    
                                    <StyledTableCell>
                                        <Tooltip 
                                            title={`${visit.village || ''}${visit.tehsil ? `, ${visit.tehsil}` : ''}\n${visit.district || ''}, ${visit.state || ''}`} 
                                            arrow
                                        >
                                            <Box>
                                                <Typography 
                                                    variant="body2"
                                                    sx={{ 
                                                        maxWidth: 150, 
                                                        overflow: 'hidden', 
                                                        textOverflow: 'ellipsis', 
                                                        whiteSpace: 'nowrap',
                                                        cursor: 'help'
                                                    }}
                                                >
                                                    {visit.village}{visit.tehsil ? `, ${visit.tehsil}` : ''}
                                                </Typography>
                                                <Typography 
                                                    variant="caption" 
                                                    color="text.secondary"
                                                    sx={{ 
                                                        maxWidth: 150, 
                                                        overflow: 'hidden', 
                                                        textOverflow: 'ellipsis', 
                                                        whiteSpace: 'nowrap',
                                                        cursor: 'help',
                                                        display: 'block'
                                                    }}
                                                >
                                                    {visit.district}, {visit.state}
                                                </Typography>
                                            </Box>
                                        </Tooltip>
                                    </StyledTableCell>
                                    
                                    <StyledTableCell>
                                        <Box>
                                            <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                                                <Phone sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                <Typography variant="body2">
                                                    {visit.farmer_contact || 'N/A'}
                                                </Typography>
                                            </Box>
                                            {visit.farmer_email && (
                                                <Box display="flex" alignItems="center" gap={0.5}>
                                                    <Email sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                    <Tooltip title={visit.farmer_email} arrow>
                                                        <Typography 
                                                            variant="caption" 
                                                            color="text.secondary"
                                                            sx={{ 
                                                                maxWidth: 150, 
                                                                overflow: 'hidden', 
                                                                textOverflow: 'ellipsis', 
                                                                whiteSpace: 'nowrap',
                                                                cursor: 'help'
                                                            }}
                                                        >
                                                            {visit.farmer_email}
                                                        </Typography>
                                                    </Tooltip>
                                                </Box>
                                            )}
                                        </Box>
                                    </StyledTableCell>
                                    
                                    <StyledTableCell>
                                        <Chip 
                                            label={visit.product_vertical?.name || 'N/A'} 
                                            size="small" 
                                            color="success"
                                            variant="outlined"
                                        />
                                    </StyledTableCell>
                                    
                                    <StyledTableCell>
                                        {visit.crop ? (
                                            <Chip 
                                                label={visit.crop.name || visit.crop} 
                                                size="small" 
                                                color="secondary"
                                                variant="outlined"
                                            />
                                        ) : (
                                            <Typography variant="caption" color="text.secondary">
                                                N/A
                                            </Typography>
                                        )}
                                    </StyledTableCell>
                                    
                                    <StyledTableCell>
                                        {visit.product_subcategory && visit.product_subcategory.length > 0 ? (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                {visit.product_subcategory.map((product, index) => (
                                                    <Box key={product.id || index}>
                                                        <Tooltip title={product.name || '-'} arrow>
                                                            <Typography 
                                                                variant="body2" 
                                                                fontWeight={500}
                                                                sx={{ 
                                                                    maxWidth: 200, 
                                                                    overflow: 'hidden', 
                                                                    textOverflow: 'ellipsis', 
                                                                    whiteSpace: 'nowrap',
                                                                    cursor: 'help'
                                                                }}
                                                            >
                                                                {product.name}
                                                            </Typography>
                                                        </Tooltip>
                                                        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                                                            {product.sku && (
                                                                <Chip 
                                                                    label={`SKU: ${product.sku}`} 
                                                                    size="small" 
                                                                    variant="outlined"
                                                                    sx={{ height: 20, fontSize: '0.65rem' }}
                                                                />
                                                            )}
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </Box>
                                        ) : (
                                            <Typography variant="caption" color="text.secondary">
                                                No products
                                            </Typography>
                                        )}
                                    </StyledTableCell>
                                    
                                    <StyledTableCell>
                                        <Typography variant="body2">
                                            {visit.farm_area_value 
                                                ? `${visit.farm_area_value} ${visit.farm_area_unit || ''}`
                                                : 'N/A'
                                            }
                                        </Typography>
                                    </StyledTableCell>
                                    
                                    <StyledTableCell>
                                        <Typography variant="body2">
                                            {formatDate(visit.plantation_date)}
                                        </Typography>
                                    </StyledTableCell>
                                    
                                    <StyledTableCell>
                                        <Tooltip title={visit.observations || '-'} arrow>
                                            <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                    maxWidth: 200, 
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    cursor: 'help'
                                                }}
                                            >
                                                {visit.observations || '-'}
                                            </Typography>
                                        </Tooltip>
                                    </StyledTableCell>
                                    
                                    <StyledTableCell>
                                        <Tooltip title={visit.recommendations || '-'} arrow>
                                            <Typography 
                                                variant="body2"
                                                sx={{ 
                                                    maxWidth: 200, 
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    cursor: 'help'
                                                }}
                                            >
                                                {visit.recommendations || '-'}
                                            </Typography>
                                        </Tooltip>
                                    </StyledTableCell>
                                    
                                    <StyledTableCell>
                                        <Tooltip title={visit.executive_name || 'N/A'} arrow>
                                            <Typography 
                                                variant="body2" 
                                                fontWeight={500}
                                                sx={{ 
                                                    maxWidth: 120, 
                                                    overflow: 'hidden', 
                                                    textOverflow: 'ellipsis', 
                                                    whiteSpace: 'nowrap',
                                                    cursor: 'help'
                                                }}
                                            >
                                                {visit.executive_name || 'N/A'}
                                            </Typography>
                                        </Tooltip>
                                    </StyledTableCell>
                                    
                                    <StyledTableCell>
                                        <Tooltip title={visit.farmer_feedback || '-'} arrow>
                                            <Typography 
                                                variant="body2"
                                                sx={{ 
                                                    maxWidth: 150, 
                                                    overflow: 'hidden', 
                                                    textOverflow: 'ellipsis', 
                                                    whiteSpace: 'nowrap',
                                                    cursor: 'help'
                                                }}
                                            >
                                                {visit.farmer_feedback || '-'}
                                            </Typography>
                                        </Tooltip>
                                    </StyledTableCell>
                                    
                                    <StyledTableCell>
                                        {visit.upload_file ? (
                                            <Tooltip title="View File">
                                                <IconButton 
                                                    size="small" 
                                                    color="primary"
                                                    onClick={() => handleFileView(visit.upload_file)}
                                                >
                                                    <AttachFile fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        ) : (
                                            <Typography variant="caption" color="text.secondary">
                                                No file
                                            </Typography>
                                        )}
                                    </StyledTableCell>
                                    
                                    <StyledTableCell>
                                        <Box>
                                            <Typography variant="body2" fontWeight={500}>
                                                {visit.added_by?.first_name} {visit.added_by?.last_name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {visit.added_by?.email}
                                            </Typography>
                                        </Box>
                                    </StyledTableCell>
                                </StyledTableRow>

                                {/* COLLAPSIBLE ROW */}
                                <TableRow>
                                    <TableCell colSpan={17} sx={{ p: 0, border: 0 }}>
                                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                            <Card
                                                sx={(theme) => ({
                                                    m: 1,
                                                    p: 2,
                                                    backgroundColor: theme.palette.mode === 'dark' 
                                                        ? alpha(theme.palette.background.paper, 0.8)
                                                        : alpha(theme.palette.primary.main, 0.02),
                                                    borderRadius: 2,
                                                })}
                                            >
                                                <Typography 
                                                    variant="h6" 
                                                    fontWeight={600} 
                                                    sx={{ mb: 2, textDecoration: 'underline' }}
                                                >
                                                    Field Visit Details - #{visit.id}
                                                </Typography>

                                                {/* Products Table */}
                                                {visit.product_subcategory && visit.product_subcategory.length > 0 ? (
                                                    <>
                                                        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                                                            Products
                                                        </Typography>
                                                        <Table size="small" sx={{ mb: 2 }}>
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableCell sx={{ fontWeight: 600 }}>Product Name</TableCell>
                                                                    <TableCell sx={{ fontWeight: 600 }}>SKU</TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {visit.product_subcategory.map((product, index) => (
                                                                    <TableRow
                                                                        key={product.id || index}
                                                                        sx={{
                                                                            '&:hover': {
                                                                                backgroundColor: alpha(theme.palette.primary.main, 0.04),
                                                                            },
                                                                        }}
                                                                    >
                                                                        <TableCell>
                                                                            <Typography variant="body2" fontWeight={500}>
                                                                                {product.name}
                                                                            </Typography>
                                                                        </TableCell>
                                                                        <TableCell>{product.sku || '-'}</TableCell>
                                                                    </TableRow>
                                                                ))}
                                                            </TableBody>
                                                        </Table>
                                                    </>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                        No products listed
                                                    </Typography>
                                                )}

                                                {/* Divider & Summary */}
                                                <Divider sx={{ my: 2 }} />
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                                                    <Box>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Visit Date:</strong> {formatDate(visit.visit_date)}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Plantation Date:</strong> {formatDate(visit.plantation_date)}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Farm Area:</strong> {visit.farm_area_value 
                                                                ? `${visit.farm_area_value} ${visit.farm_area_unit || ''}`
                                                                : 'N/A'}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Product Vertical:</strong> {visit.product_vertical?.name || 'N/A'}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            <strong>Crop:</strong> {visit.crop?.name || visit.crop || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Farmer Name:</strong> {visit.farmer_name || 'N/A'}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Contact:</strong> {visit.farmer_contact || 'N/A'}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Email:</strong> {visit.farmer_email || 'N/A'}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Location:</strong> {visit.village}{visit.tehsil ? `, ${visit.tehsil}` : ''}, {visit.district}, {visit.state}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            <strong>Executive:</strong> {visit.executive_name || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ maxWidth: '300px' }}>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Observations:</strong>
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
                                                            {visit.observations || 'N/A'}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Recommendations:</strong>
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
                                                            {visit.recommendations || 'N/A'}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Feedback:</strong>
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                                                            {visit.farmer_feedback || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Total Products:</strong> {visit.product_subcategory?.length || 0}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            <strong>Added By:</strong> {visit.added_by?.first_name} {visit.added_by?.last_name}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Card>
                                        </Collapse>
                                    </TableCell>
                                </TableRow>
                                    </Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <Box 
                    sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 8
                    }}
                >
                    <Typography 
                        variant="h6" 
                        color="text.secondary"
                        align="center"
                    >
                        {searchQuery 
                            ? "No field visits found matching your search"
                            : "No field visit records available"
                        }
                    </Typography>
                </Box>
            )}

            {pagination && onPageChange && pagination.total_pages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Pagination count={pagination.total_pages} page={pagination.current_page} onChange={onPageChange} color="primary" />
                </Box>
            )}
            <Box
  sx={{
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    mt: 3,
  }}
>
  <Typography variant="body2" color="text.secondary">
    {pagination.total_items > 0
      ? `Showing ${(pagination.current_page - 1) * pagination.page_size + 1} - ${Math.min(
          pagination.current_page * pagination.page_size,
          pagination.total_items
        )} of ${pagination.total_items}`
      : "No courses"}
  </Typography>
</Box>
        </Box>
    );
};

export default FieldVisitTable;

